import { z } from "zod";

import { roleSchema } from "../auth/auth";
import { DEFAULT_OFFLINE_AFTER_MINUTES } from "../devices/devices";
import { incidentStatusSchema, incidentTypeSchema, severitySchema } from "../incidents/incidents";
import { tenantScopeSchema } from "../common/tenant";

/**
 * Counters behind the cards at the top of the dashboard.
 *
 * `offline` and `neverSeen` are separate because they call for different
 * actions: one sensor stopped talking, the other was never installed.
 */
export const dashboardKpisSchema = z.object({
  devicesTotal: z.number().int().nonnegative(),
  devicesOnline: z.number().int().nonnegative(),
  devicesOffline: z.number().int().nonnegative(),
  devicesNeverSeen: z.number().int().nonnegative(),
  /** Devices whose last reading sits outside the target range of their asset. */
  devicesOutOfRange: z.number().int().nonnegative(),
  incidentsOpen: z.number().int().nonnegative(),
  /** Incidents opened in the last 24 hours, whatever their current status. */
  incidentsLast24h: z.number().int().nonnegative(),
});

export type DashboardKpis = z.infer<typeof dashboardKpisSchema>;

/**
 * An incident as the dashboard shows it: enough context to decide whether to
 * walk over to the fridge, without a second request per row.
 */
export const dashboardIncidentSchema = z.object({
  id: z.uuid(),
  deviceId: z.uuid(),
  devEui: z.string(),
  assetName: z.string().nullable(),
  siteName: z.string().nullable(),
  organizationName: z.string(),
  type: incidentTypeSchema,
  status: incidentStatusSchema,
  severity: severitySchema,
  startedAt: z.iso.datetime(),
  triggerValue: z.number().nullable(),
});

export type DashboardIncident = z.infer<typeof dashboardIncidentSchema>;

export const DASHBOARD_RECENT_INCIDENTS = 5;

export const dashboardSummarySchema = z.object({
  /** Null for a super admin reading the whole platform. */
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  scope: roleSchema,
  kpis: dashboardKpisSchema,
  recentIncidents: z.array(dashboardIncidentSchema),
  /** Minutes without an uplink behind the online/offline split. */
  offlineAfterMinutes: z.number().int().positive().default(DEFAULT_OFFLINE_AFTER_MINUTES),
  generatedAt: z.iso.datetime(),
});

export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

export const dashboardSummaryQuerySchema = tenantScopeSchema;
export type DashboardSummaryQuery = z.infer<typeof dashboardSummaryQuerySchema>;
